import React, { useEffect, useState } from "react";
import { Gift, Copy, Share2, Check, Loader2, Sparkles, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n, sym = "₹") => `${sym}${Math.round(Number(n || 0))}`;

export default function ReferAndEarn() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/me/referral`, { credentials: "include" });
      if (r.ok) setData(await r.json());
      else setData(null);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // Pre-fill from URL or localStorage referral param
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const stored = localStorage.getItem("sm_pending_ref");
      if (urlRef) {
        localStorage.setItem("sm_pending_ref", urlRef.toUpperCase());
        setRedeemCode(urlRef.toUpperCase());
      } else if (stored) {
        setRedeemCode(stored);
      }
    } catch { /* ignore */ }
  }, []);

  const handleCopy = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Code copied!");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const handleShare = async () => {
    if (!data) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}${data.share_url || "/?ref=" + data.code}`;
    const text = `Join Super Morsel with my code ${data.code} — get a welcome wallet bonus on your first order. ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Super Morsel", text, url }); return; } catch { /* user dismissed */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Share text copied — paste it anywhere!"); }
    catch { toast.error("Sharing not supported on this device"); }
  };

  const handleRedeem = async () => {
    const code = (redeemCode || "").trim().toUpperCase();
    if (!code) { toast.error("Enter a referral code"); return; }
    setRedeeming(true);
    try {
      const r = await fetch(`${API}/me/referral/redeem`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || "Couldn't redeem code");
      toast.success(`Linked! Place your first order of ${fmtMoney(json.min_first_order_amount)}+ to unlock ${fmtMoney(json.referee_credit_on_qualification)}.`, { duration: 6000 });
      try { localStorage.removeItem("sm_pending_ref"); } catch { /* ignore */ }
      setRedeemCode("");
      await load();
    } catch (e) {
      toast.error(e.message || "Couldn't redeem code");
    } finally { setRedeeming(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center justify-center" data-testid="refer-and-earn-loading">
        <Loader2 size={16} className="animate-spin text-gray-400" />
      </div>
    );
  }
  if (!data || !data.program_enabled) return null; // soft-pause: hide entirely

  const earned = data.total_credits_earned || 0;
  const referrerCredit = data.rules?.referrer_credit || 0;
  const refereeCredit = data.rules?.referee_credit || 0;
  const minOrder = data.rules?.min_first_order_amount || 0;
  const showRedeemBox = !data.referred_by_code && (data.user_orders || 0) === 0;
  const lockReason = !data.eligible_to_share
    ? `Place ${data.min_orders_to_share || 1}+ order${(data.min_orders_to_share || 1) > 1 ? "s" : ""} totalling ${fmtMoney(data.min_lifetime_spend_to_share)} to unlock sharing.`
    : null;

  return (
    <div data-testid="refer-and-earn-card" className="rounded-2xl overflow-hidden border border-emerald-100 shadow-sm bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <Gift size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-extrabold text-gray-900">Refer & Earn</h3>
              <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                <Sparkles size={10} /> {fmtMoney(referrerCredit)} per referral
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Friends get {fmtMoney(refereeCredit)} on a {fmtMoney(minOrder)}+ first order. You get {fmtMoney(referrerCredit)} when they do.
            </p>
          </div>
        </div>

        {data.eligible_to_share ? (
          <div className="mt-4 bg-white border border-emerald-100 rounded-xl p-3 sm:p-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Your code</p>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <code className="text-base sm:text-xl font-black tracking-wider text-emerald-700 bg-emerald-50 border border-dashed border-emerald-300 rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 select-all" data-testid="refer-code">
                {data.code}
              </code>
              <Button onClick={handleCopy} size="sm" variant="outline" className="text-xs h-8" data-testid="refer-copy">
                {copied ? <Check size={12} className="mr-1" /> : <Copy size={12} className="mr-1" />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button onClick={handleShare} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="refer-share">
                <Share2 size={12} className="mr-1" /> Share
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase text-emerald-700/70 font-semibold">Friends joined</p>
                <p className="text-xl sm:text-2xl font-extrabold text-emerald-700 mt-0.5" data-testid="refer-count">{data.total_referred || 0}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase text-amber-700/70 font-semibold">Earned</p>
                <p className="text-xl sm:text-2xl font-extrabold text-amber-700 mt-0.5" data-testid="refer-earned">{fmtMoney(earned)}</p>
              </div>
            </div>
            {data.recent_referrals?.length > 0 && (
              <div className="mt-4 -mx-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2 px-1">Recent</p>
                <ul className="divide-y divide-gray-50">
                  {data.recent_referrals.slice(0, 5).map((r) => (
                    <li key={r.referral_id} className="flex items-center justify-between py-2 px-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-900 truncate">{r.referee_email || r.referee_name || "—"}</p>
                        <p className="text-[10px] text-gray-400">{(r.created_at || "").slice(0, 10)}</p>
                      </div>
                      <StatusPill status={r.status} amount={r.referrer_credit} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 bg-white border border-dashed border-gray-200 rounded-xl p-4 flex items-start gap-3" data-testid="refer-locked">
            <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
              <Lock size={13} />
            </div>
            <div className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">Sharing locked.</span> {lockReason}
            </div>
          </div>
        )}

        {showRedeemBox && (
          <div className="mt-4 bg-white border border-gray-100 rounded-xl p-3 sm:p-4" data-testid="refer-redeem-box">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Got a code from a friend?</p>
            <div className="flex items-center gap-2">
              <Input
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="ABHAY-2X4F"
                className="text-xs uppercase tracking-wider font-semibold"
                data-testid="refer-redeem-input"
              />
              <Button
                onClick={handleRedeem}
                disabled={redeeming || !redeemCode.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9"
                data-testid="refer-redeem-btn"
              >
                {redeeming ? <Loader2 size={12} className="animate-spin" /> : "Redeem"}
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Codes can only be redeemed before your first order.</p>
          </div>
        )}

        {data.referred_by_code && (
          <p className="text-[10px] text-gray-400 mt-3" data-testid="refer-already-linked">
            Linked to a referrer ({data.referred_by_code}). Bonus arrives after your first qualifying order.
          </p>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status, amount }) {
  const map = {
    credited: { label: `+₹${amount || 0}`, cls: "bg-emerald-100 text-emerald-700" },
    pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
    reversed: { label: "Reversed", cls: "bg-red-100 text-red-700" },
    blocked: { label: "Blocked", cls: "bg-gray-100 text-gray-500" },
  };
  const m = map[status] || { label: status, cls: "bg-gray-100 text-gray-500" };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}
